import React, { useEffect, useState } from "react";
import AxiosInstance from "../authorization/AxiosInstance";
import ImageUploader from "./ImageUploader";

const EMPTY = { name: "", role: "", bio: "", imageUrl: "", photoSide: "left", order: 0 };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <span className="font-bold text-lg text-gray-800">{title}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="overflow-y-auto px-6 py-4 flex-1">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", rows }) {
  const cls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400";
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {rows
        ? <textarea name={name} value={value} onChange={onChange} rows={rows} className={cls} />
        : <input name={name} type={type} value={value} onChange={onChange} className={cls} />}
    </div>
  );
}

export default function LeaderProfilesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState(null);

  const fetchItems = () => {
    setLoading(true);
    AxiosInstance.get("/leader-profiles")
      .then(r => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchItems(); }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === "order" ? Number(value) : value }));
  };

  const openCreate = () => { setForm(EMPTY); setModal("create"); setError(null); };
  const openEdit = item => {
    setForm({
      name: item.name || "",
      role: item.role || "",
      bio: item.bio || "",
      imageUrl: item.imageUrl || "",
      photoSide: item.photoSide || "left",
      order: item.order ?? 0,
    });
    setEditId(item.id); setModal("edit"); setError(null);
  };
  const closeModal = () => { setModal(null); setEditId(null); setError(null); };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      if (modal === "create") await AxiosInstance.post("/leader-profiles", form);
      else await AxiosInstance.patch(`/leader-profiles/${editId}`, form);
      fetchItems(); closeModal();
    } catch (e) { setError(e?.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    try { await AxiosInstance.delete(`/leader-profiles/${id}`); setDeleteId(null); fetchItems(); }
    catch { setError("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Leadership Section</h2>
        <button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">
          + Add Leader
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        These appear in the Leadership section on the landing page. Set <strong>Photo Side</strong> to <em>left</em> or <em>right</em> to control image placement. Use <strong>Order</strong> to control sequence.
      </p>

      {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

      {loading ? <div className="text-gray-400 text-sm">Loading…</div> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 uppercase text-xs">
                <th className="text-left px-4 py-3 font-semibold">#</th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Photo Side</th>
                <th className="text-left px-4 py-3 font-semibold">Order</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No leaders added yet</td></tr>
              )}
              {items.map((item, i) => (
                <tr key={item.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{item.name}</td>
                  <td className="px-4 py-3 text-gray-500">{item.role || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${item.photoSide === "right" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                      {item.photoSide}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{item.order}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(item)} className="text-blue-600 hover:text-blue-800 text-xs font-semibold">Edit</button>
                      {deleteId === item.id ? (
                        <span className="flex gap-1">
                          <button onClick={() => handleDelete(item.id)} className="text-red-600 text-xs font-semibold">Confirm</button>
                          <button onClick={() => setDeleteId(null)} className="text-gray-400 text-xs">Cancel</button>
                        </span>
                      ) : (
                        <button onClick={() => setDeleteId(item.id)} className="text-red-500 hover:text-red-700 text-xs font-semibold">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal === "create" ? "Add Leader" : "Edit Leader"} onClose={closeModal}>
          {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
          <Field label="Name *" name="name" value={form.name} onChange={handleChange} />
          <Field label="Role / Title" name="role" value={form.role} onChange={handleChange} />
          <Field label="Bio" name="bio" value={form.bio} onChange={handleChange} rows={4} />
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
            <ImageUploader
              value={form.imageUrl}
              onChange={(url) => setForm(f => ({ ...f, imageUrl: url }))}
              folder="leaders"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Photo Side</label>
            <select
              name="photoSide"
              value={form.photoSide}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="left">Left (border top-left)</option>
              <option value="right">Right (border bottom-right)</option>
            </select>
          </div>
          <Field label="Order" name="order" value={form.order} onChange={handleChange} type="number" />
          <div className="flex justify-end gap-3 mt-2">
            <button onClick={closeModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.name} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
